import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { useRoleProtection } from '../hooks/useRoleProtection';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '../utils/roleUtils';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  // Role protection options
  checkRoleProtection?: boolean;
  targetUserId?: string;
  action?: 'edit' | 'deactivate' | 'delete';
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  checkRoleProtection = false,
  targetUserId,
  action
}) => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const { canPerformAction } = useRoleProtection();

  if (!user) {
    return <>{fallback}</>;
  }

  let hasAccess = false;

  // Check role protection first if enabled
  if (checkRoleProtection && targetUserId && action) {
    hasAccess = canPerformAction(action, targetUserId);
  } else {
    // Standard permission checking
    if (permission) {
      hasAccess = hasPermission(user, permission, currentBusiness?.id);
    } else if (permissions) {
      if (requireAll) {
        hasAccess = hasAllPermissions(user, permissions, currentBusiness?.id);
      } else {
        hasAccess = hasAnyPermission(user, permissions, currentBusiness?.id);
      }
    } else {
      // If no permissions specified, allow access
      hasAccess = true;
    }
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGate; 